import React, { useRef, useState } from 'react';
import { useSpc } from '../../context/SpcContext';
import { AchievementCard } from '../Achievements';
import MembershipCard from '../MembershipCard';
import { fileToResizedDataURL } from '../../lib/images';
import AvatarCrop from '../AvatarCrop';
import { Camera, Trash2, Trophy, Gift, Sparkles, Bell } from 'lucide-react';
import { CountUp } from '../ui';
import { ScInfoBadge } from '../ScInfoBadge';
import { isSCSession } from '../../lib/sessionInfo';

function RewardsCta({ state, go }) {
  const points = state.member.points || 0;
  const lifetime = state.member.lifetimePoints || 0;
  const rewards = (state.rewards || []).filter(r => r.active).sort((a, b) => a.cost - b.cost);
  const affordable = rewards.filter(r => points >= r.cost);
  // Show one "recommended": the biggest reward the member can afford, else the cheapest to aim for
  const highlight = affordable.length ? affordable[affordable.length - 1] : rewards[0];
  const nextGap = highlight && points < highlight.cost ? highlight.cost - points : 0;
  return (
    <div className="card span-12 rewards-cta" data-testid="rewards-cta-card">
      <div className="rewards-cta-inner">
        <div className="rewards-cta-left">
          <span className="pill yellow" style={{ alignSelf: 'flex-start' }}><Sparkles size={12} style={{ marginRight: 4, verticalAlign: '-2px' }} /> PRIZES</span>
          <div className="rewards-cta-balance">
            <strong><CountUp value={points} /></strong>
            <span>SPC points available</span>
          </div>
          <p className="rewards-cta-sub">
            {lifetime > points ? `${lifetime} points earned lifetime · ` : ''}
            Turn attendance, challenges and referrals into real prizes.
          </p>
          <button className="btn" onClick={() => go('leaderboard')} data-testid="rewards-cta-btn">Open Prizes</button>
        </div>
        <div className="rewards-cta-right">
          {highlight ? (
            affordable.includes(highlight) ? (
              <>
                <div className="rewards-cta-highlight-tag">You can redeem</div>
                <div className="rewards-cta-highlight-title"><Gift size={16} /> {highlight.title}</div>
                {highlight.description && <p className="rewards-cta-highlight-desc">{highlight.description}</p>}
                <div className="rewards-cta-highlight-cost">{highlight.cost} pts</div>
              </>
            ) : (
              <>
                <div className="rewards-cta-highlight-tag">Next prize</div>
                <div className="rewards-cta-highlight-title"><Gift size={16} /> {highlight.title}</div>
                <p className="rewards-cta-highlight-desc">Just {nextGap} more points to unlock this.</p>
                <div className="rewards-cta-progress"><span style={{ width: `${Math.min(100, Math.round((points / highlight.cost) * 100))}%` }} /></div>
                <div className="rewards-cta-highlight-cost">{highlight.cost} pts</div>
              </>
            )
          ) : (
            <>
              <div className="rewards-cta-highlight-tag">Coming soon</div>
              <div className="rewards-cta-highlight-title"><Gift size={16} /> Prizes launching soon</div>
              <p className="rewards-cta-highlight-desc">Keep earning points — new club prizes drop into the shop shortly.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function AvatarUploader({ user, updateAvatar, toast }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const initials = (user?.name || 'S').split(' ').map(x => x[0]).slice(0, 2).join('').toUpperCase();
  const pick = async (files) => {
    const f = (files || [])[0];
    if (!f) return;
    try { setCropSrc(await fileToResizedDataURL(f, 1400, 0.88)); }
    catch (e) { toast('Could not read that photo'); }
  };
  const confirmCrop = async (dataUrl) => {
    setCropSrc(null);
    setBusy(true);
    try { await updateAvatar(dataUrl); }
    catch (e) { toast('Could not save that photo'); }
    setBusy(false);
  };
  return (
    <div className="avatar-uploader">
      <button type="button" className="avatar-big" data-testid="profile-avatar" onClick={() => fileRef.current?.click()} aria-label="Change profile photo">
        {user?.avatar ? <img src={user.avatar} alt={user.name} /> : <span>{initials}</span>}
        <span className="avatar-edit"><Camera size={15} /></span>
      </button>
      <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} data-testid="avatar-file-input" onChange={(e) => { pick(e.target.files); e.target.value = ''; }} />
      <div className="avatar-actions">
        <button type="button" className="btn secondary" data-testid="upload-photo-btn" disabled={busy} onClick={() => fileRef.current?.click()}>{busy ? 'Uploading…' : user?.avatar ? 'Change photo' : 'Upload photo'}</button>
        {user?.avatar && <button type="button" className="btn ghost" data-testid="remove-photo-btn" disabled={busy} onClick={() => updateAvatar(null)}><Trash2 size={14} /> Remove</button>}
      </div>
      {cropSrc && <AvatarCrop src={cropSrc} shape="circle" onConfirm={confirmCrop} onCancel={() => setCropSrc(null)} />}
    </div>
  );
}

export default function Profile({ go }) {
  const { state, role, user, openModal, openRaceHub, openSPC, cancelBooking, toast, isAchievementUnlocked, updateAvatar, startCheckout, setMemberPin } = useSpc();
  const [showPast, setShowPast] = useState(false);
  const mine = state.sessions.filter(s => s.mine);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const mineUpcoming = mine.filter(s => !s.dateISO || new Date(s.dateISO).getTime() >= today.getTime());
  const minePast = mine.filter(s => s.dateISO && new Date(s.dateISO).getTime() < today.getTime());

  if (role === 'member') {
    const unlocked = state.achievementCatalogue.filter(a => isAchievementUnlocked(a.id)).slice(0, 4);
    return (
      <>
        <div className="section-head"><div><h2>My SPC</h2><p>Membership, bookings and preferences.</p></div></div>
        <div className="grid">
          <div className="card span-12"><MembershipCard user={user} tier={state.member.tier} memberSince={state.member.memberSince} validThru={state.member.nextPayment} toast={toast} setMemberPin={setMemberPin} /></div>
          <div className="card span-12" data-testid="safety-card-summary">
            <span className="pill yellow">🆘 MY SAFETY CARD</span>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginTop: 10 }}>
              <div>
                <h3 style={{ margin: 0 }}>{user?.safetyCardComplete ? 'On file' : 'Not completed yet'}</h3>
                <p>Emergency contact & ICE details, only seen by coaches/session leaders during a session — required to book.</p>
              </div>
              <button className="btn secondary" onClick={() => openModal('safetyCard')} data-testid="edit-safety-card-btn">{user?.safetyCardComplete ? 'Edit' : 'Complete now'}</button>
            </div>
          </div>
          <div className="card span-12">
            <h3>My bookings</h3>
            <div className="list" data-testid="upcoming-bookings-list">{mineUpcoming.length ? mineUpcoming.map(s => <div className="item" key={s.id}><div><strong>{s.title}{isSCSession(s) && <ScInfoBadge testId={`sc-info-badge-${s.id}`} className="sc-alert-badge-inline" />}</strong><div className="meta">{s.date} · {s.time}</div></div><button className="btn secondary" onClick={() => cancelBooking(s.id)}>Cancel</button></div>) : <div className="empty">Nothing upcoming booked.</div>}</div>
            {minePast.length > 0 && (
              <>
                <button className="btn ghost" style={{ marginTop: 14 }} onClick={() => setShowPast(v => !v)} data-testid="toggle-past-bookings-btn">{showPast ? 'Hide past bookings' : `Show past bookings (${minePast.length})`}</button>
                {showPast && (
                  <div className="list" style={{ marginTop: 10 }} data-testid="past-bookings-list">
                    {minePast.map(s => <div className="item" key={s.id}><div><strong>{s.title}</strong><div className="meta">{s.date} · {s.time}</div></div><span className="pill soft">Completed</span></div>)}
                  </div>
                )}
              </>
            )}
          </div>
          <RewardsCta state={state} go={go} />
          <div className="card span-12">
            <span className="pill yellow">SPC ACHIEVEMENT CABINET</span>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginTop: 10 }}>
              <div><h3 data-testid="achievement-cabinet-count">{state.unlockedAchievements.length} {state.unlockedAchievements.length === 1 ? 'achievement' : 'achievements'} unlocked</h3><p>Sessions, challenges, racing, community, referrals, membership and special SPC milestones.</p></div>
              <button className="btn" data-testid="open-achievement-cabinet-btn" onClick={() => openModal('achievements')}>Open Cabinet</button>
            </div>
            <div className="achievement-grid" style={{ marginTop: 12 }}>
              {unlocked.map(a => <AchievementCard key={a.id} a={a} />)}
            </div>
          </div>
          <div className="card span-6">
            <span className="pill yellow">COACHING</span>
            {state.member.isCoached
              ? (<><h3 style={{ marginTop: 10 }}>{state.member.coachingPlan}</h3><p><strong>Coach:</strong> {state.member.coachName}<br /><strong>Status:</strong> {state.member.coachingStatus}</p></>)
              : (<><h3 style={{ marginTop: 10 }}>SPC Member</h3><p>You currently have club membership without an active coaching package.</p></>)}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
              {state.member.isCoached
                ? <button className="btn secondary" onClick={() => openModal('messages')} data-testid="profile-message-coach-btn">Message your coach</button>
                : <button className="btn secondary" onClick={() => go('benefits')} data-testid="profile-explore-coaching-btn">Explore coaching</button>}
              <button className="btn ghost" onClick={() => openSPC('plans')} data-testid="profile-training-plans-btn">Training Plans</button>
              <button className="btn ghost" onClick={() => openSPC('planplus')} data-testid="profile-planplus-btn">Plan+</button>
            </div>
          </div>
          <div className="card span-6">
            <span className="pill yellow">MEMBERSHIP & BILLING</span>
            <h3 style={{ marginTop: 10 }}>{state.member.billingPlan}</h3>
            <p><strong>{state.member.billingAmount || 'Active subscription'}</strong><br />{state.member.nextPayment ? <>Next payment: {state.member.nextPayment}<br /></> : null}{state.member.paymentMethod ? `Payment method: ${state.member.paymentMethod}` : ''}</p>
            <p className="meta">Rolling 12-month membership from the date you join. No Club joining fee.</p>
            {state.member.isCoached && <div className="notice"><strong>Coached athlete:</strong> {state.member.coachingPlan}<br /><strong>Joining fee:</strong> £29 one-off</div>}
            {!state.member.hasStripeSubscription && state.member.nextPayment && (
              <div className="notice" data-testid="legacy-membership-notice">Your membership is tracked manually. Renew via card any time to switch to automatic billing.</div>
            )}
            <div className="hero-actions">
              {!state.member.hasStripeSubscription ? (
                <button className="btn" onClick={startCheckout} data-testid="renew-membership-btn">Renew now · £29/year</button>
              ) : (
                <button className="btn" onClick={() => openModal('memberBilling')}>Manage billing</button>
              )}
              <button className="btn secondary" onClick={() => openModal('changePlan')}>Explore</button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // admin centre — count anyone who isn't an admin (members + coaches)
  const isActive = (m) => m.role !== 'Admin' && String(m.status || '').toLowerCase() === 'active';
  const isPending = (m) => m.role !== 'Admin' && String(m.approvalStatus || '').toLowerCase() === 'pending';
  const activeMembers = state.members.filter(isActive).length;
  const pendingApprovals = state.members.filter(isPending).length;
  const waitTotal = Object.values(state.adminData.waitlists).reduce((a, b) => a + (+b || 0), 0);
  const tools = [
    ['Members', 'Approve, remove and manage members. Assign custom roles (Coach, Run Leader, Committee…) and control who can create sessions.', 'Manage members', 'adminMembers'],
    ['Renewals', 'Quick-glance list of members renewing in the next 30 days, soonest first, with a one-tap reminder.', 'View renewals', 'adminRenewals'],
    ['Discount codes', 'Create sign-up discount codes — 100% off gives a free membership instantly, no card needed.', 'Manage codes', 'adminDiscountCodes'],
    ['Club settings', 'WhatsApp group link + welcome tour tagline shown to new members.', 'Open settings', 'adminSettings'],
    ['Check in members', 'Type or ask for a member\'s PIN to mark them in. Points per check-in are editable inside.', 'Open check-in', 'adminCheckIn'],
    ['Club challenge', 'Set a daily, weekly or monthly challenge with a target and unit. Members see it and their own progress.', 'Set challenge', 'adminChallenge'],
    ['SPC Breaking News', 'Write a title & message shown as a pop-up the next time members log in. Use for really big announcements.', 'Write breaking news', 'adminBreakingNews'],
    ['Recent broadcasts', 'Sent the wrong Announcement, Breaking News, Challenge or Session alert? Remove it from every member\'s notification bell here.', 'Manage broadcasts', 'adminBroadcasts'],
    ['Recent admin actions', 'Deleted a session, cancelled a series or removed a member by mistake? See recent destructive actions here and undo them.', 'View & undo', 'adminAuditLog'],
    ['Attendance', 'Record attendance and see session turnout.', 'Manage attendance', 'adminAttendance'],
    ['Waiting lists', 'See demand and promote members into spaces.', 'Manage waitlists', 'adminWaitlists'],
    ['Event history', 'Completed sessions and attendance history.', 'View history', 'adminEventHistory'],
    ['Challenge results', 'Current leaderboard and participation.', 'View results', 'adminChallengeResults'],
    ['SPC Points', 'Points ledger and manual adjustments.', 'Open ledger', 'adminPoints'],
    ['Reward redemptions', 'Approve and fulfil claimed rewards.', 'View redemptions', 'adminRedemptions'],
    ['Referrals', 'Qualified and pending member referrals.', 'Track referrals', 'adminReferrals'],
    ['Sell moderation', 'Review member kit listings.', 'Moderate listings', 'go:community'],
    ['Reporting', 'Snapshot of bookings, members and rewards.', 'View report', 'adminReporting'],
    ['Subscriptions & Billing', 'Stripe subscriptions, failed payments, credits, changes and invoices.', 'Manage billing', 'adminBilling'],
    ['Achievements', 'Manage achievement rules, automatic unlocks and special manual awards.', 'Manage achievements', 'adminAchievements'],
    ['SPC Services', 'Manage coaching, plans, services, member offers and featured products.', 'Manage services', 'adminServices'],
    ['Email templates', 'Edit the wording and subject of booking, reminder and welcome emails members receive.', 'Edit emails', 'adminEmailTemplates'],
    ['Race Hub', 'Open and manage the Race Hub experience from inside the SPC app.', 'Open Race Hub', 'raceHub'],
    ['Danger zone — Clean sweep', 'Wipe ALL club data: members, sessions, listings, chats, games, points, ledgers, rewards and history. Keeps admin accounts and the achievement cabinet.', 'Open clean sweep', 'adminReset'],
    ['Go-live reset — keep bookings', 'One-time pre-launch reset: wipes beta members, points, ranks, achievements, marketplace, chats and games — but keeps your sessions/events schedule and its bookings intact.', 'Open go-live reset', 'adminLaunchReset']
  ];
  const handle = (key) => {
    if (key === 'go:community') return go('community');
    if (key === 'raceHub') return openRaceHub('view');
    return openModal(key);
  };
  return (
    <>
      <div className="section-head"><div><h2>Admin centre</h2><p>Members, attendance, waiting lists, rewards, referrals and reporting in one place.</p></div></div>
      <div className="grid">
        <div className="card span-12"><div className="member-id"><AvatarUploader user={user} updateAvatar={updateAvatar} toast={toast} /><div><h3 style={{ marginTop: 0, marginBottom: 2 }}>{user?.name}</h3><p style={{ margin: 0 }}>Administrator · {user?.email}</p></div></div></div>
        <div className="card span-4"><div className="stat"><strong>{activeMembers}</strong><span>Active members</span></div></div>
        <div className="card span-4"><div className="stat"><strong>{waitTotal}</strong><span>Waiting list places</span></div></div>
        <div className="card span-4"><div className="stat"><strong>{pendingApprovals}</strong><span>Awaiting approval</span></div></div>
        <div className="card span-12">
          <h3>Full admin centre</h3>
          <p>Everything you need to run SPC — attendance, waiting lists, rewards, coaching, billing and reporting.</p>
          <div className="grid" style={{ marginTop: 10 }}>
            {tools.map(([title, desc, cta, key]) => (
              <div className="card span-4" key={title} data-testid={`admin-tool-card-${key}`}><h3>{title}</h3><p>{desc}</p><button className="btn secondary" data-testid={`admin-tool-btn-${key}`} onClick={() => handle(key)}>{cta}</button></div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
